<template>
  <div class="ms-combobox ms-editor" :class="[{'ms-validate': true}]">
    <div v-if="title && showTitle" class="combo-title flex">
      <div class="combo-title__text">{{ title }}</div>
    </div>
    <label v-if="label" class="label">{{ label }}</label>
    <div
      class="flex-row combo-box-input"
      :class="[
        {error: errorMessage, disabled: disabled}, 
        hasBorder ? 'border' : '',
        {'combo-box-input-flexible': allowWrap}
      ]"
    >
      <div v-if="leftIcon" :class="['icon24 left-icon', leftIcon]"></div>
      <!-- TODO => Thêm các prop để show tooltip -->
      <div class="p-hidden-accessible">
        <input
          ref="input"
          class="input flex no-press"
          :title="internalText"
          :placeholder="placeholder"
          :class="{disabled: disabled}"
          :disabled="disabled"
          @keydown="onKeyDown"
          @keydown.f3="quickSearchClick"
          @keydown.f9="showQuickAdd(false, true)"
          @blur="onBlur()"
        />
      </div>
      <div class="p-multiselect-label-container input flex" 
           :class="{
             'p-multiselect-flexible-height': allowWrap,
             'p-multiselect-one-item-per-line': oneItemPerLine
           }">
        <div 
          class="p-multiselect-label"
          :class="{
            'p-multiselect-label-wrap': allowWrap,
            'p-multiselect-label-one-item-per-line': oneItemPerLine
          }"
          :style="{ 
            'max-height': maxDisplayLines ? `calc(${maxDisplayLines} * 1.5rem)` : 'none',
            'overflow-y': maxDisplayLines ? 'auto' : 'hidden'
          }"
        >
          <slot name="value" :value="modelValue" :placeholder="placeholder">
            <template v-if="display === 'comma'">
              <div class="p-multiselect-text-label">{{ internalText || '' }}</div>
            </template>
            <template v-else-if="display === 'chip'">
              <div v-for="item of chipSelectedItems" :key="item[valueField]" class="p-multiselect-token">
                <slot name="chip" :value="item">
                  <span class="p-multiselect-token-label" 
                       :class="{'p-multiselect-token-label-wrap': allowWrap}" 
                       :title="item[displayField]">{{ item[displayField] }}</span>
                </slot>
                <slot v-if="!disabled" name="removetokenicon" class="p-multiselect-token-icon" :item="item">
                  <span class="icon24 close p-multiselect-token-icon" @click="removeOption(item, $event)" />
                </slot>
              </div>
              <template v-if="!modelValue || modelValue.length === 0">{{ placeholder || '' }}</template>
            </template>
          </slot>
        </div>
      </div>
      <div
        v-if="quickAdd"
        class="quickAdd"
        @click="showQuickAdd"
        @keydown.enter="showQuickAdd"
        :class="[
          {hide: disabled},
          hasBorder ? dbOptionHeight + '-border' : dbOptionHeight,
          heightOption == 2 ? '' : hasBorder ? 'quickAdd-small-border' : 'quickAdd-small',
        ]"
      ></div>
      <div
        @click="triggerClick"
        :class="{
          disabled: disabled,
          expand: expanded,
          'icon-combo--dropdown multiple': heightOption == 2,
          'icon-combo--dropdown-small-border multiple': heightOption == 1 && hasBorder,
          'icon-combo--dropdown-small multiple': heightOption == 1 && !hasBorder,
        }"
      >
      
      </div>
    </div>
    <div v-if="errorMessage && isShowText && !isGridEditor" class="error-text">
      {{ errorMessage }}
    </div>
    <ms-combo-box-dropdown
      v-if="expanded"
      ref="dropdownPanel"
      :topx="dropdownTopx"
      :leftx="dropdownLeftx"
      :max-widthx="dropdownMaxWidthx"
      :min-widthx="dropdownMinWidthx"
      :columnx="columns"
      :combo-type="comboType"
      :scroll-top="dropdownScrollTop"
      :loading="loading"
      :quick-add="quickAdd"
      :quick-search="quickSearch"
      :is-check-multiple="true"
      :show-footer-buttons="showFooterButtons"
      @scrollend="dropdownScrollEnd"
      @add-new="showQuickAdd"
      @cancel="handleCancelFooter"
      @confirm="handleConfirmFooter"
      :maxItemsDisplay="maxItemsDisplay"
    >
      <ms-combo-box-item
        v-for="(item, index) in internalDataSource"
        :key="item[valueField]"
        :combo-type="comboType"
        :item="item"
        :is-selected="item && item['selected']"
        :columnx="columns"
        :value-field="valueField"
        :is-check-multiple="true"
        :display-field="displayField"
        :class="{'ms-combobox-item--highlight': typeAheadPointer === index}"
        @mousedown="onItemClick(item, $event, index)"
      ></ms-combo-box-item>
      <template v-slot:quickSearch>
        <div
          v-if="quickSearch"
          class="quickSearch"
          :class="[{hide: disabled}, heightOption == 2 ? '' : 'quickSearch-small']"
          @click="quickSearchClick"
          @keydown.enter="quickSearchClick"
        >
          <div class="button icon24 quick-search"></div>
          <div class="txt-quick-search">Tìm nhanh (F3)</div>
        </div>
      </template>
    </ms-combo-box-dropdown>
  </div>
</template>
<script>
import {watch, ref, nextTick, getCurrentInstance, onMounted, onBeforeUnmount, onBeforeMount, computed} from 'vue';

import MsComboBoxDropdown from './MsComboBoxDropdown.vue';
import MsComboBoxItem from './MsComboBoxItem.vue';
import {useValidateControl} from '@/composables/validateControl.js';
import commonFn from '@/commons/commonFunction.js';
import msBaseComponent from '@/components/base/MsBaseComponent.vue';
import popupUtil from '@/commons/popupUtil.js';

export default {
  components: {
    MsComboBoxDropdown,
    MsComboBoxItem,
  },
  extends: msBaseComponent,
  props: {
    modelValue: {},
    customTooltip: {
      default: null,
      type: [Object],
    },
    placeholder: {
      default: null,
      type: [Number, String],
    },
    maxItemsDisplay: {
      default: 5,
      type: Number,
    },
    columns: {
      type: Array,
      default: () => [],
    },
    /**
     * Trường làm value
     * CreatedBy LTDAT 15.06.2020
     */
    valueField: {
      type: String,
      default: null,
    },
    /**
     * Trường làm text display
     * CreatedBy LTDAT 15.06.2020
     */
    displayField: {
      type: String,
      default: null,
    },
    // Dữ liệu của combobox
    data: {
      default: () => [],
      type: Array,
    },
    // Độ rộng của 1 item
    itemSize: {
      default: 28,
      type: Number,
    },
    // Chỉ nhận giá trị có trong danh sách
    forceSelection: {
      default: true,
      type: Boolean,
    },
    // Là combotree hay không
    isTree: {
      default: false,
      type: Boolean,
    },
    // Parent Field
    idProperty: {
      default: 'id',
      type: String,
    },
    // Field chứa parentID
    parentIdProperty: {
      default: 'parent_id',
      type: String,
    },
    // Không cho phép chọn trường là cha
    notSelectionParent: {
      default: false,
      type: Boolean,
    },
    // Độ rông của dropdown tính bằng px
    dropdownWidth: {
      default: null,
      type: [String, Number],
    },
    // Mode query lấy dữ liệu từ server hay client
    queryMode: {
      type: String,
      default: 'local',
    },
    remoteFilter: {
      type: Boolean,
      default: false,
    },
    // Gán giá trị text lúc ban đầu
    initText: {
      default: null,
      type: [Number, String],
    },
    // chỉ cho phép chọn không cho phép nhập text
    selectOnly: {
      default: false,
      type: Boolean,
    },
    autoLoad: {
      default: false,
      type: Boolean,
    },
    display: {},
    mappingField: {
      default: null,
    },
    firstExpandLoad: {
      default: false,
      type: Boolean,
    },
    keyField: {
      // default: '__vKeyValue',
      type: String,
    },
    maxLength: {
      default: null,
      type: Number,
    },
    leftIcon: {
      type: String,
    },
    /**
     * Câu hình quicksearch
     */
    quickSearch: {
      type: String,
    },
    /**
     * Dữ liệu truyền kèm quicksearch
     */
    quickSearchParam: {
      type: Object,
      default: null,
    },
    /**
     * Câu hình add
     */
    quickAdd: {
      type: String,
      default: null,
    },
    isShowText: {
      type: Boolean,
      default: true,
    },
    /*
     * Hàm show ra câu thông báo riêng theo từng trường hợp
     * ví dụ: customWarring {rules:'required', mes: 'Tài sản này là của pvduy'}
     */
    customWarring: {
      type: Object,
      default: null,
    },
    /**
     * Danh sách các trường dữ liệu lấy về
     */
    requestField: {
      type: String,
      default: null,
    },
    showTitle: {
      type: Boolean,
      default: false,
    },
    /**
     * Danh sách các trường dữ liệu lấy về
     */
    title: {
      type: String,
      default: null,
    },
    /**
     * custom lại tham số filter
     */
    buildFilter: {
      type: Function,
      default: null,
    },

    isReload: {
      type: Boolean,
      default: false,
    },
    /**
     * size paging
     */
    pageSize: {
      type: Number,
      default: 10,
    },
    /**
     * Giá trị bản ghi tất cả
     */
    selectAllValue: {
      type: [Number, String],
      default: -1,
    },
    /**
     * Text hiển thị tất cả
     */
    selectAllText: {
      type: String,
      default: null,
    },
    /**
     * Có hiển thị bản ghi tất cả không
     */
    showSelectAll: {
      type: Boolean,
      default: false,
    },
    // Xác định xem input có border hay không?
    hasBorder: {
      default: true,
      type: Boolean,
    },
    // Xác định các rule validate
    // Mặc định của combo sẽ là force select
    rules: {
      default: null,
      type: Array,
    },
    disabled: {
      default: false,
      type: Boolean,
    },
    separator: {
      default: ';',
      type: String,
    },
    isMultiple: {
      type: Boolean,
      default: true,
    },
    display: {
      type: String,
      default: 'comma',
    },
    maxSelectedLabel: {
      type: Number,
      default: 2,
    },

    /**
     * Giá trị bản ghi tổng hợp
     */
    selectSummaryValue: {
      type: [Number, String],
      default: -1,
    },
    /**
     * Text hiển thị tổng hợp
     */
    selectSummaryText: {
      type: String,
      default: null,
    },
    /**
     * Có hiển thị bản ghi tổng hợp không
     */
    showSelectSummary: {
      type: Boolean,
      default: false,
    },

    /**
     * Xác định nếu chọn tất cả thì sẽ không chọn tất cả các bản ghi, chỉ chọn bản ghi tất cả
     */
    selectOnlyAllItem: {
      type: Boolean,
      default: false,
    },
    /**
     * Hiển thị footer buttons (Hủy và Đồng ý)
     */
    showFooterButtons: {
      type: Boolean,
      default: false,
    },
    /**
     * Số dòng tối đa hiển thị trong combobox khi có nhiều items được chọn
     */
    maxDisplayLines: {
      type: Number,
      default: 5,
    },
    /**
     * Cho phép xuống dòng khi items tràn độ rộng
     */
    allowWrap: {
      type: Boolean,
      default: false,
    },
    /**
     * Mỗi phần tử hiển thị trên một dòng riêng biệt
     */
    oneItemPerLine: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, {emit}) {
    const internalDataSource = ref([]);
    const input = ref({});
    const expanded = ref(false);
    const {proxy} = getCurrentInstance();
    const internalText = ref('');
    const loading = ref(false);
    const dropdownScrollTop = ref(0);

    const dropdownTopx = ref(0);
    const dropdownLeftx = ref(0);
    const comboType = ref(1);
    const dropdownPanel = ref({});
    const cacheSelected = ref(new Map());
    const typeAheadPointer = ref(0);
    const internalSelected = ref([]);

    const onBlur = evt => {
      const me = this;
      validate();
      emit('blur', evt);
    };

    const rebuildSelectedItems = (cacheData = null) => {
      let data = internalDataSource.value || [];
      if (!data || !data.length) {
        return;
      }

      data.forEach(item => {
        item.selected =
          internalSelected.value &&
          internalSelected.value.some(x => x[props.displayField] === item[props.displayField]);
      });
      // const splitInternalText = internalText?.value?.split(props.separator).filter(x => x) || '';
      // if (splitInternalText.length > 0 && splitInternalText.length !== cacheSelected.size) {
      //   if (!cacheData) {
      //     cacheData = new Map();
      //     internalDataSource.value.forEach(x => cacheData.set(x[props.displayField], x));
      //   }

      //   cacheSelected.value.clear();
      //   splitInternalText.forEach(text => {
      //     if (cacheData.has(text)) {
      //       const item = cacheData.get(text);
      //       item.selected = true;
      //       cacheSelected.value.set(item[props.valueField], item);
      //     }
      //   });
      // } else {

      // }
    };
    /**
     * thêm bản ghi chọn tất cả vào source
     * Modified by: nhthai 23.7.2024 - Bổ sung text Tất cả cho tất cả các cột
     */
    const appendSelectAllItem = () => {
      const me = this;
      if (!props.showSelectAll) {
        return;
      }

      let firstItem = internalDataSource.value[0];

      if (props.showSelectSummary && internalDataSource.value.length > 1) {
        firstItem = internalDataSource.value[1];
      }

      if (firstItem && firstItem[props.valueField] !== props.selectAllValue) {
        const allItem = {};

        allItem[props.displayField] = props.selectAllText;
        allItem[props.valueField] = props.selectAllValue;

        // nhthai - 23.7.2024: Bổ sung text Tất cả cho tất cả các cột. Loại bỏ set text cho cột displayField vì đã được set ở trên
        if (props && props.columns && props.columns.length > 0) {
          props.columns.forEach(column => {
            if (column.dataField != props.displayField) {
              allItem[column.dataField] = proxy.$ms.constant.SpecialValue.AllText;
            }
          });
        }

        allItem['selected'] = false;
        // // vvkiet - 24.06.2021: Bổ sung text Tất cả cho tất cả các cột
        // me.columns.forEach(column => {
        //   allItem[column.dataField] = me._selectAllText;
        // });

        internalDataSource.value.unshift(allItem);
      }
    };

    /**
     * thêm bản ghi chọn tổng hợp vào source
     * nhhoang1 02.07.2024
     * modified by: NHThai 23.7.2024: Bổ sung text Tổng hợp cho tất cả các cột
     */
    const appendSelectSumamryItem = () => {
      const me = this;

      if (!props.showSelectSummary) {
        return;
      }

      const firstItem = internalDataSource.value[0];

      if (firstItem && firstItem[props.valueField] !== props.selectSummaryValue) {
        const allItem = {};

        allItem[props.displayField] = props.selectSummaryText;
        allItem[props.valueField] = props.selectSummaryValue;

        // nhthai 23.7.2024: Bổ sung text Tổng hợp cho tất cả các cột. Loại bỏ set text cho cột displayField vì đã được set ở trên
        if (props && props.columns && props.columns.length > 0) {
          props.columns.forEach(column => {
            if (column.dataField != props.displayField) {
              allItem[column.dataField] = proxy.$ms.constant.SpecialValue.SummaryText;
            }
          });
        }

        allItem['selected'] = false;

        internalDataSource.value.unshift(allItem);
      }
    };

    const updateSelected = selected => {
      emit('update:selected', selected, proxy.indexCombo);
      //internalSelected.value = selected;
    };
    /**
     * Click vào icon xổ xuống
     */
    const triggerClick = e => {
      const me = proxy;
      const expand = expanded.value;

      // disable thì không làm gì
      if (me.disabled) {
        return;
      }

      if (!expand) {
        // TODO => Query internal
        query();
        changeDropdownPosition();
      }

      expanded.value = !expand;
      nextTick(() => {
        adjustScroll();
      });

      // focus input
      input.value.focus();
    };

    /**
     * Thay đổi position khi mở dropdown
     */
    const changeDropdownPosition = () => {
      const me = proxy;
      let comboEl = me.$el;

      if (comboEl) {
        comboEl = comboEl.getElementsByClassName('combo-box-input')[0];
      }

      if (comboEl) {
        const comboElRect = comboEl.getBoundingClientRect();
        const comboMenuEl = me.$el;

        const windowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;

        const windowHeight = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;

        me.dropdownLeftx = comboElRect.left;
        me.dropdownTopx = comboElRect.top + comboElRect.height + 2;
        me.dropdownMaxWidthx = me.dropdownMinWidthx = me.dropdownWidth ? me.dropdownWidth : comboElRect.width;

        // Nếu chạm phải thì align sang trái
        if (comboElRect.left + me.dropdownMaxWidthx > windowWidth) {
          me.dropdownLeftx = comboElRect.right - me.dropdownMaxWidthx;
        }

        // Nếu chạm bottom thì align top
        let maxItemsDisplay = me.maxItemsDisplay;
        if (me.columns && me.columns.length > 0) {
          maxItemsDisplay += 1;
        }
        if (comboElRect.bottom + me.itemSize * maxItemsDisplay + 2 > windowHeight) {
          me.dropdownTopx =
            comboElRect.top - (me.itemSize * Math.min(maxItemsDisplay, me.internalDataSource.length) + 16);
        }
      }
    };

    const query = () => {
      switch (props.queryMode) {
        case 'local':
          localQuery();
          break;
        case 'remote':
          remoteQuery();
          break;
        default:
          break;
      }
    };

    const onEsc = e => {
      const me = proxy;

      // ẩn dropdown và stop event nếu đang expand
      if (me.expanded) {
        expanded.value = false;
        e.preventDefault();
        e.cancel = true;
      }
    };

    const onKeyDown = e => {
      if (e.keyCode !== proxy.$ms.constant.KeyCode.Tab) {
        e.preventDefault();
        e.stopPropagation();
      }

      switch (e.keyCode) {
        case proxy.$ms.constant.KeyCode.Enter:
          if (typeAheadPointer.value >= 0 && typeAheadPointer.value < internalDataSource.value.length) {
            onItemClick(internalDataSource.value[typeAheadPointer.value]);
          }
          return;
        case proxy.$ms.constant.KeyCode.Up:
          if (typeAheadPointer.value === 0) return;
          typeAheadPointer.value -= 1;
          adjustScroll();
          return;

        case proxy.$ms.constant.KeyCode.Down:
          if (!expanded.value) {
            typeAheadPointer.value = 0;
            triggerClick();
          } else {
            if (
              lastRequestParam &&
              lastRequestParam.end &&
              internalDataSource.value.length === typeAheadPointer.value + 1
            ) {
            } else {
              typeAheadPointer.value += 1;
              adjustScroll();
            }
          }
          return;

        case proxy.$ms.constant.KeyCode.ESC:
          onEsc(e);
          break;
      }
      emit(proxy.$ms.constant.CustomEvent.MsKeydown, e);
    };

    const getScroller = () => {
      if (expanded.value) {
        const dd = dropdownPanel.value;
        if (dd && dd.$el && typeof dd.$el.querySelector === 'function') {
          return dd.$el.querySelector('.scroller-overlay');
        }
      }
      return null;
    };

    const adjustScroll = () => {
      const row = dropdownPanel?.value?.$el?.querySelector('.combobox-item');
      if (row) {
        const rowHeight = row.getBoundingClientRect().height;
        const scroller = getScroller();
        if (scroller) {
          // scroller.scrollToItem(typeAheadPointer, true);
          scroller.scrollTop = Number.parseInt(rowHeight - 0.5) * typeAheadPointer.value;
        }
      }
    };

    /**
     * Query data có sẵn ở local
     */
    const localQuery = () => {
      // if (!lastRequestParam || !lastRequestParam.end) {
      const payload = {};
      payload.skip = internalDataSource.value.length;
      payload.take = props.pageSize;
      payload.pageAppend = true;

      const start = payload.skip;
      const count = Math.min(props.data.length, payload.take + start);
      const isEnd = count === start;

      if (!isEnd) {
        for (let i = payload.skip; i < count; i++) {
          internalDataSource.value.push(props.data[i]);
        }
      } else {
        payload.end = true;
      }
      lastRequestParam = payload;
      rebuildSelectedItems();
      // }
    };

    /**
     * Khởi tạo data
     */
    const initData = value => {
      let cacheData = null;

      // Gán item được chọn
      internalSelected.value =
        props.selected ||
        props.modelValue?.split(props.separator)?.map(function (x) {
          let ob = {};
          ob[props.displayField] = x;
          return ob;
        }) ||
        [];

      if (props.queryMode === 'remote') {
        if (lastRequestParam && lastRequestParam.pageAppend) {
          cacheData = new Map();
          internalDataSource.value.concat(value).forEach(x => {
            if (!cacheData.has(x[props.valueField])) {
              cacheData.set(x[props.displayField], x);
            }
          });
          internalDataSource.value = [...cacheData.values()];
        } else {
          // Nếu có data mới sẽ clear cache do data thêm qua quick add sẽ được add trực tiếp vào list
          internalDataSource.value = value;
          cacheSelected.value.clear();
        }

        if (value.length < props.pageSize && lastRequestParam) {
          lastRequestParam.end = true;
        }
        // Rebuild lại các selected item nếu có
        rebuildSelectedItems(cacheData);
      } else {
        localQuery();
      }

      appendSelectAllItem();

      // thêm bản ghi tổng hợp nhhoang1 02.07.2024
      appendSelectSumamryItem();

      // Rebuild lại các selected item nếu có nhhoang1 02.07.2024
      if ((props.showSelectAll && props.selectOnlyAllItem) || props.showSelectSummary) {
        rebuildSelectedItems(cacheData);
      }

      // Xử lý gán lại cái item đã được chọn
      if (internalSelected.value && internalSelected.value.length > 0) {
        const mappingSelectedDisplayField = internalSelected.value.map(x => x[props.displayField]);
        internalSelected.value = [];
        internalSelected.value.unshift(
          ...internalDataSource.value.filter(x => mappingSelectedDisplayField.includes(x[props.displayField])),
        );
        emit('update:selected', internalSelected.value);
      }
    };
    let lastRequestParam = null;

    /**
     * Query lên server
     */
    const remoteQuery = () => {
      if (!lastRequestParam || props.isReload) {
        const me = proxy;
        const payload = {};
        payload.skip = 0;
        payload.take = props.pageSize;
        payload.pageAppend = false;
        let columns = [props.displayField, props.valueField].concat(props.columns.map(x => x.dataField));

        columns = columns.distinctKey(columns);
        payload.columns = columns.join(',');

        if (props.modelValue) {
          const modelValueSplit = props.modelValue.split(props.separator);
          const selected = modelValueSplit.map(x => x);

          if (selected.length > 0) {
            Object.assign(payload, {
              selectedItem: JSON.stringify({[props.displayField]: selected}),
            });
          }
        }
        lastRequestParam = payload;
        emit('comboboxLoadData', payload);
      }
    };

    /**
     * Query thêm data nếu scroll xuống cuối
     */
    const dropdownScrollEnd = () => {
      if (props.queryMode === 'local') {
        query();
      } else {
        if (lastRequestParam && !lastRequestParam.end) {
          if (lastRequestParam.skip && lastRequestParam.skip === internalDataSource.value.length) {
            return;
          }

          const payload = lastRequestParam;
          payload.skip =
            internalDataSource.value.length - (props.showSelectAll ? 1 : 0) - (props.showSelectSummary ? 1 : 0); // đối với TH thêm dòng tất cả hoặc tổng hợp thì không cộng vào số lượng bản ghi cần lấy nhhoang1 07.09.2024

          payload.pageAppend = true;
          emit('comboboxLoadData', payload);
        }
      }
    };
    /**
     * Tích chọn select hoặc ngừng select 1 item
     */
    const effectItem = item => {
      const key = item[props.valueField];
      item.selected = !item.selected;
      // Kiêm tra xem có trường đấy rồi thì xóa đi ( tức là bỏ tích )
      if (cacheSelected.value.has(key)) {
        cacheSelected.value.delete(key);
        //internalSelected.value.remove(item);
      }
      // Chưa có thì đang check nên thêm vào
      else {
        cacheSelected.value.set(key, item);
        //internalSelected.value.push(item);
      }
      if (item.selected) {
        internalSelected.value.push(item);
      } else {
        internalSelected.value =
          internalSelected.value?.filter(x => x[props.displayField] != item[props.displayField]) || [];
      }
    };

    /**
     * Build text hiển thị trên ô input
     */
    const buildText = () => {
      let rebuildText = '';
      // const sizes = cacheSelected.size;
      const sizes = internalSelected.value.length;
      let index = 0;
      //Nếu có tích chọn vào ô tất cả thì hiển thị text tất cả
      if (internalSelected.value.find(x => x[props.valueField] == props.selectAllValue)) {
        rebuildText = props.selectAllText;
      } else {
        internalSelected.value
          //Text khi chọn Tất cả không thêm vào input
          .filter(
            x =>
              x[props.valueField] != props.selectAllValue ||
              // nếu selectOnlyAllItem == true thì sẽ hiện thị bản ghi tất cả nhhoang1 02.07.2024
              (x[props.valueField] == props.selectAllValue && props.selectOnlyAllItem),
          )
          .forEach((value, key) => {
            if (index === sizes - 1) {
              rebuildText += `${value[props.displayField]}`;
            } else {
              rebuildText += `${value[props.displayField]}${props.separator}`;
            }
            index++;
          });
      }
      return rebuildText;
    };
    /*
    ndhieu1 11/6/2024
    Tick chọn hoặc bỏ tick chọn Tất cả
    */
    const effectSelectAllItem = item => {
      //Tick All

      if (item[props.valueField] == props.selectAllValue) {
        let selectValue = item['selected'] ?? false;

        let selectAllRow = [];
        // xử lý nếu chỉ tích chọn bản ghi tất cả  nhhoang1 02.07.2024
        if (props.selectOnlyAllItem) {
          // bỏ tích
          selectAllRow = internalDataSource.value.filter(
            y =>
              y['selected'] == true &&
              y[props.valueField] != props.selectAllText &&
              (!props.showSelectSummary ||
                (props.showSelectSummary && y[props.valueField] != props.selectSummaryValue)),
          );

          selectAllRow.forEach(x => {
            x.selected = true;
            effectItem(x);
          });

          effectItem(item);
        } else {
          //Tick chọn tất cả
          selectAllRow = internalDataSource.value.filter(
            y =>
              y['selected'] == selectValue &&
              (!props.showSelectSummary ||
                (props.showSelectSummary && y[props.valueField] != props.selectSummaryValue)),
          );

          selectAllRow.forEach(x => {
            x.selected = selectValue;
            effectItem(x);
          });
        }

        // nếu tích chọn tất cả thì bỏ tích chọn giá trị tổng hợp nhhoang1 02.07.2024
        if (props.showSelectSummary) {
          //Lấy dòng Tổng hợp
          let selectSummaryItem = internalDataSource.value.find(x => x[props.valueField] == props.selectSummaryValue);

          if (selectSummaryItem) {
            //Bỏ tick dòng Tổng hợp
            selectSummaryItem.selected = true;
            effectItem(selectSummaryItem);
          }
        }
      } else if (props.showSelectSummary && item[props.valueField] == props.selectSummaryValue) {
        effectSelectSummaryItem(item);
      } else {
        //Bỏ tick dòng tất cả nhhhoang1 02.07.2024
        unCheckSelectAllItem();

        //Bỏ tick dòng tổng hợp nhhhoang1 02.07.2024
        unCheckSelectSummaryItem();

        //Tick dòng được chọn
        effectItem(item);
      }
    };

    /**
     * Tick chọn hoặc bỏ tick chọn Tổng hợp
     * nhhoang1 02.07.2024
     */
    const effectSelectSummaryItem = item => {
      if (item[props.valueField] == props.selectSummaryValue) {
        // lấy ds các dòng không phải là dòng tổng hợp
        var selectAllRow = internalDataSource.value.filter(y => y[props.valueField] != props.selectSummaryValue);

        // bỏ tích chọn tất cả
        selectAllRow.forEach(x => {
          x.selected = true;

          effectItem(x);
        });
      } else {
        //Bỏ tick dòng tổng hợp nhhhoang1 02.07.2024
        unCheckSelectSummaryItem();
      }

      //Tick dòng được chọn
      effectItem(item);
    };

    /**
     * Bỏ tích chọn dòng tổng hợp
     * nhhoang1 02.07.2024
     */
    const unCheckSelectSummaryItem = () => {
      // lấy  dòng tổng hợp
      let selectSummaryRow = internalDataSource.value.find(y => y[props.valueField] == props.selectSummaryValue);

      if (selectSummaryRow) {
        //Bỏ tick dòng tổng hợp
        selectSummaryRow.selected = true;
        effectItem(selectSummaryRow);
      }
    };

    /**
     * Bỏ tích chọn dòng tất cả
     * nhhoang1 02.07.2024
     */
    const unCheckSelectAllItem = () => {
      // lấy  dòng tất cả
      let selectAllRow = internalDataSource.value.find(y => y[props.valueField] == props.selectAllValue);

      if (selectAllRow) {
        //Bỏ tick dòng tất cả
        selectAllRow.selected = true;
        effectItem(selectAllRow);
      }
    };

    /**
     * Xử lý click vào item
     */
    const onItemClick = (item, event, index) => {
      //Nếu có tick chọn Tất cả
      if (props.showSelectAll) {
        effectSelectAllItem(item);
      } else if (props.showSummaryAll) {
        effectSelectSummaryItem(item);
      } else {
        effectItem(item);
      }

      internalText.value = buildText();
      //TODO updateModel theo value field nếu có nhu cầu
      emit('update:modelValue', internalText.value);
      if (index !== undefined && index !== null) {
        typeAheadPointer.value = index;
      }

      const selected = [...internalSelected.value];
      // emit thêm sự kiện để bắt giá trị vừa chọn - tvhien 14.12.2022
      emit('onSelectedItem', item, selected);
      emit('update:selected', selected);
      nextTick(() => {
        setTimeout(() => {
          input.value.focus();
        }, 0);
      });
    };
    
    /**
     * Xử lý remove item đã chọn
     */
    const removeOption = (item, $event) => {
      effectItem(item);
      internalText.value = buildText();
      emit('update:modelValue', internalText.value);
      const selected = [...internalSelected.value];
      emit('update:selected', selected);
    };
    /**
     *  danh sách chip
     */
    const chipSelectedItems = computed(() => {
      if (props.modelValue && props.modelValue !== '') {
        const chips =
          internalSelected.value.length != 0
            ? internalSelected.value
            : internalText.value.split(props.separator)?.map(function (x) {
                let ob = {};
                ob[props.displayField] = x;
                return ob;
              });

        return chips;
      }
    });
    /**
     * Xử lý click ra ngoài
     */
    const clickOutSide = e => {
      const me = proxy;
      if (
        !commonFn.hasParent(e.target, me.$el) &&
        dropdownPanel.value &&
        dropdownPanel.value.$el &&
        !commonFn.hasParent(e.target, dropdownPanel.value.$el)
      ) {
        expanded.value = false;
      }
    };
    const {errorMessage, handlerValidateCombobox, getTitleError, clearValidate} = useValidateControl({
      props,
    });
    const validate = (hasValidateSave, triggerDisplayError) => {
      if (!proxy.isGridEditor) {
        return handlerValidateCombobox(hasValidateSave, triggerDisplayError);
      }
      return '';
    };

    /**
     * Call back gọi từ form quick add, search
     */
    const callbackAddData = (data, type) => {
      const fullData = props.queryMode === 'remote' ? internalDataSource.value : props.data;
      const findValidData = arr => {
        switch (type) {
          case 'quickSearch':
            [...internalSelected.value].forEach(x => (x.selected = false));
            //cacheSelected.clear();
            internalSelected.value = [];
            return arr.filter(item => {
              item.selected = true;
              //let cacheItem = item;
              //cacheSelected.set(item[props.valueField], item);
              internalSelected.value.push(item);
              var existed = fullData.find(x => x[props.valueField] === item[props.valueField]);

              if (existed) {
                existed.selected = true;
                //cacheItem = existed;
              }

              //cacheSelected.set(cacheItem[props.valueField], item);
              return !existed;
            });
          //Default sẽ vào quick add do quick add dùng các form danh mục đã có
          default:
            return arr.filter(item => {
              item.selected = true;
              var existed = fullData.find(x => x[props.valueField] === item[props.valueField]);
              // if (!cacheSelected.has(item[props.valueField])) {
              //   cacheSelected.set(item[props.valueField], item);
              // }

              if (existed) {
                existed.selected = true;
                return false;
              } else {
                internalSelected.value.push(item);
              }
              return true;
            });
        }
      };
      if (Array.isArray(data)) {
        internalDataSource.value.unshift(...findValidData(data));
      } else {
        internalDataSource.value.unshift(...findValidData([data]));
      }

      internalText.value = buildText();
      emit('update:modelValue', internalText.value);

      // 30.08.2022 TPNam : Sửa thêm update seleted
      const selected = [...internalSelected.value];
      emit('update:selected', selected);
      //Trỏ scroll về thằng đầu tiên
      typeAheadPointer.value = 0;
    };

    /**
     * Click vào icon quick add
     */
    const showQuickAdd = () => {
      const me = proxy;
      // Thực hiện query luôn lần đầu để chạy data select
      if (props.queryMode === 'remote') {
        query();
      }
      if (!me.loading) {
        if (!me.quickAdd) {
          return;
        }
        expanded.value = false;
        const param = {...(me.quickSearchParam || {})};
        param.mode = me.$ms.constant.FormState.Add;
        if (me.internalText) {
          param.quickAddText = me.internalText;
        }

        // Modified By NXCHIEN 01.06.2022 Sửa lại do base xử lý check options.submit
        param.options = {
          submit: callbackAddData,
          type: 'quickAdd',
        };

        // TODO => show popup quick add ở đây
        popupUtil.show(me.quickAdd, param);
      }
    };

    /**
     * Click vào icon quicksearch
     */
    const quickSearchClick = e => {
      const me = proxy;
      if (me.quickSearch) {
        if (me.disabled) {
          return;
        }
        me.expanded = false;
        const param = {...(me.quickSearchParam || {})};
        popupUtil.show(
          me.quickSearch,
          Object.assign(param, {
            /**
             * Sự kiện callback khi chọn item từ form tìm nhanh
             * Modified By NXCHIEN 01.06.2022 Sửa lại do base xử lý check options.submit
             */
            options: {
              submit: callbackAddData,
              type: 'quickSearch',
            },
            selected: props.modelValue,
            close: function () {
              me.$refs.input.focus();
            },
          }),
        );
      }
    };

    const getValue = () => {
      return internalText.value;
    };

    /**
     * Tìm item theo array selected
     */
    const findItemByArray = () => {
      if (!proxy.internalDataSource || proxy.internalDataSource.length === 0) {
        return false;
      }

      let arr = proxy.internalSelected;
      let rs = [];

      let invalid = arr.some(x => {
        let items = proxy.internalDataSource.filter(item => item[proxy.displayField] == x[proxy.displayField]);

        rs.append(items);
        return !items.length;
      });

      return !invalid && rs;
    };

    /**
     * Tìm item theo text truyền vào
     * HTHIEP 01.07.2022
     */
    const findItemByText = queryText => {
      if (!queryText || !proxy.internalDataSource || proxy.internalDataSource.length === 0) {
        return false;
      }

      let arr = queryText.split(props.separator);
      let rs = [];

      let invalid = arr.some(txt => {
        let items = proxy.internalDataSource.filter(item => item[proxy.displayField] == txt);

        rs.append(items);
        return !items.length;
      });

      return !invalid && rs;
    };

    /**
     * hàm autoload dữ liệu combo
     * pqkhanh 03.07.2024
     */
    const doQueryInternal = () => {
      const me = this;
      const event = {preventDefault: false, sender: me};
      emit('beforeQuery', event);
      if (event.preventDefault) {
        return false;
      }
      // Đoạn này check xem nếu khác querystring thì thực hiện load data từ server
      if (props.queryMode === 'remote') {
        remoteQuery();
      } else {
        localQuery();
      }
    };
    // Hook
    onMounted(() => {
      document.addEventListener('click', clickOutSide);
      if (props.autoLoad) {
        doQueryInternal();
      }
    });
    onBeforeUnmount(() => {
      const me = proxy;
      expanded.value = false;
      if (dropdownPanel.value && dropdownPanel.value.$el) {
        dropdownPanel.value.$el.remove();
      }
      document.removeEventListener('click', clickOutSide);
    });
    watch(
      () => props.data,
      (value, oldValue) => {
        if (props.isReload || (Array.isArray(value) && value.length > 0)) {
          initData(value);
        }
      },
      {immediate: true},
    );
    watch(
      () => props.selected,
      (value, oldValue) => {
        if (value) {
          proxy.internalSelected = value;
        }
      },
      {immediate: true},
    );

    watch(
      () => props.modelValue,
      (value, oldValue) => {
        if ((value && value !== internalText.value) || (!value && oldValue)) {
          internalText.value = value;
          let listSelected = value?.split(props.separator) || [];
          // internalSelected.value = internalDataSource.value.filter(x => listSelected.includes(x[props.displayField]));
          //Lấy đúng thứ tự hiển thị như đã chọn trước đó pdtai 30.12.2024 sửa bug 406252
          internalSelected.value = [];
          listSelected.forEach(dataField => {
            let item = internalDataSource.value.find(y => y[props.displayField] == dataField);
            if (item) {
              internalSelected.value.push(item);
            }
          });

          // Nhthai 21.8.2024: Sửa lỗi nếu chọn tất cả thì thêm all bản ghi vào lst selected
          if (internalText.value == props.selectAllText && props.autoLoad == false) {
            internalSelected.value = internalDataSource.value;
          }
          
          rebuildSelectedItems();
          //updateSelected([...cacheSelected.value.values()]);
        }
      },
      {immediate: true},
    );
    onBeforeMount(() => {
      const me = proxy;
      if (me.isTree) {
        me.comboType = 3;
      } else if (me.columns && me.columns.length !== 0) {
        me.comboType = 2;
      } else {
        me.comboType = 1;
      }
    });

    const getMetaData = () => {
      return JSON.parse(JSON.stringify([...internalSelected.value]));
      // return JSON.parse(JSON.stringify([...cacheSelected.value.values()]));
    };

    const heightOption = computed(() => {
      return proxy.$store?.state?.dbOption?.dbOptions.HeightOption || 2;
    });

    /**
     * Xử lý khi nhấn nút Hủy trong footer
     */
    const handleCancelFooter = () => {
      // Bỏ tất cả các option đang chọn
      internalDataSource.value.forEach(item => {
        if (item.selected) {
          item.selected = false;
        }
      });
      
      // Xóa các item đã chọn trong cache và array
      cacheSelected.value.clear();
      internalSelected.value = [];
      
      // Cập nhật text hiển thị
      internalText.value = '';
      emit('update:modelValue', '');
      emit('update:selected', []);
      
      // Đóng dropdown và không lưu thay đổi
      expanded.value = false;
    };

    /**
     * Xử lý khi nhấn nút Đồng ý trong footer
     */
    const handleConfirmFooter = () => {
      // Đóng dropdown và lưu thay đổi
      expanded.value = false;
      
      // Cập nhật lại text và emit sự kiện
      internalText.value = buildText();
      emit('update:modelValue', internalText.value);
      
      const selected = [...internalSelected.value];
      emit('update:selected', selected);
      emit('confirm', selected);
    };

    return {
      internalDataSource,
      query,
      onEsc,
      expanded,
      triggerClick,
      input,
      dropdownLeftx,
      dropdownTopx,
      internalText,
      loading,
      dropdownScrollEnd,
      comboType,
      dropdownScrollTop,
      validate,
      getTitleError,
      clearValidate,
      errorMessage,
      onItemClick,
      dropdownPanel,
      showQuickAdd,
      quickSearchClick,
      typeAheadPointer,
      onKeyDown,
      getMetaData,
      cacheSelected,
      onBlur,
      getValue,
      findItemByText,
      findItemByArray,
      internalSelected,
      chipSelectedItems,
      appendSelectAllItem,
      appendSelectSumamryItem,
      removeOption,
      doQueryInternal,
      heightOption,
      handleCancelFooter,
      handleConfirmFooter,
    };
  },
};
</script>
<style lang="scss" scoped>
@import '@/assets/scss/components/MsCombobox.scss';
</style>
